import React, { CSSProperties, useEffect, useState } from 'react';
import {
    Text,
    Stack,
    IconButton,
    IStackTokens,
    IStackStyles,
    IIconProps,
} from '@fluentui/react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { appStyles } from '../../../helpers';
import { selectTranslate } from '../../../store/selectors/ui';
import { useWizard } from '../../../wizard';
import { ServerFilesDialog } from '../../dialogs';
import WizardFiles from '../files';
import { PageHeading } from '../../common/pageHeading';
import { WizardFooter } from '../footer';
import {
    selectDestinationFolderPath,
    selectFilesToUpload,
} from '../../../store/selectors/app';
import { loadDndFilesThunk } from '../../../store/actions/dnd/thunks';
import { selectTeamsPathUploadFiles } from '../../../store/selectors/auth';
import { selectDefaultFolderPath } from '../../../store/selectors/defaultFolder';
import { selectTeamsContext } from '../../../store/selectors/conversation';

export type DestinationFolder = string | null;

const containerTokens: IStackTokens = { childrenGap: 10 };

const textStyle: CSSProperties = {
    fontSize: 14,
    fontStyle: 'normal',
    marginTop: 0,
};

const uploadContainerStyles: IStackStyles = { root: { alignItems: 'center' } };
const uploadButtonContainerTokens: IStackTokens = { childrenGap: 8 };

const folderIconProps: IIconProps = {
    iconName: 'FabricFolder',
};

const wizardFilesContainerTokens: IStackTokens = { childrenGap: 15 };

export const DndFilesStep = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const translate = useAppSelector(selectTranslate);
    const filesToUpload = useAppSelector(selectFilesToUpload);
    const destinationFolderPath = useAppSelector(selectDestinationFolderPath);
    const defaultUploadFolder = useAppSelector(selectTeamsPathUploadFiles);
    const defaultFolderPath = useAppSelector(selectDefaultFolderPath);
    const teamsContext = useAppSelector(selectTeamsContext);
    const isChannelOrTeam = Boolean(
        teamsContext?.channel?.id || teamsContext?.team?.groupId,
    );

    const [foldersDialogHidden, setFoldersDialogHidden] = useState(true);
    const { setWizardStep } = useWizard();

    let uploadPath: DestinationFolder;
    if (isChannelOrTeam && defaultFolderPath) {
        uploadPath = destinationFolderPath
            ? destinationFolderPath
            : defaultFolderPath;
    } else {
        uploadPath = destinationFolderPath
            ? destinationFolderPath
            : defaultUploadFolder;
    }

    useEffect(() => {
        if (filesToUpload.length > 0) {
            return;
        }
        dispatch(loadDndFilesThunk());
    }, [filesToUpload.length]);

    const onDestinationClick = (): void => {
        setFoldersDialogHidden(false);
    };

    return (
        <>
            <Stack tokens={containerTokens}>
                <PageHeading text="Share files" />
                <Text style={textStyle}>{translate('sharefiles_desc')}</Text>
                <Stack
                    horizontal={true}
                    styles={uploadContainerStyles}
                    tokens={uploadButtonContainerTokens}
                >
                    <IconButton
                        styles={appStyles.folderIconButton}
                        iconProps={folderIconProps}
                        onClick={onDestinationClick}
                    />
                    <Text>{uploadPath}</Text>
                </Stack>
                <Stack tokens={wizardFilesContainerTokens}>
                    <WizardFiles
                        wizardPos={-1}
                        onEdit={setWizardStep}
                        onDestination={onDestinationClick}
                        hideDestinationFolder={true}
                    />
                </Stack>
            </Stack>
            <WizardFooter />
            <ServerFilesDialog
                hidden={foldersDialogHidden}
                onDismiss={setFoldersDialogHidden}
                renderFiles={false}
            />
        </>
    );
};
